import { AlertCircle, KeyRound, Loader2, Server } from 'lucide-react';
import { useEffect, useState } from 'react';

import type {
  GraFxIntegrationSettingsInput,
  GraFxIntegrationSettingsView,
  SftpIntegrationSettingsInput,
  SftpIntegrationSettingsView,
} from '@grafx-vdp/shared';

import {
  useGrafxIntegrationQuery,
  useSftpIntegrationQuery,
  useUpdateGrafxIntegrationMutation,
  useUpdateSftpIntegrationMutation,
} from '@/api/hooks.js';
import { Button } from '@/components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { toast } from '@/components/ui/sonner.js';

interface GrafxFormState {
  authBaseUrl: string;
  grafxBaseUrl: string;
  clientId: string;
  clientSecret: string;
  environmentId: string;
  dataConnectorId: string;
}

interface SftpFormState {
  host: string;
  port: string;
  username: string;
  password: string;
}

const EMPTY_GRAFX_FORM: GrafxFormState = {
  authBaseUrl: '',
  grafxBaseUrl: '',
  clientId: '',
  clientSecret: '',
  environmentId: '',
  dataConnectorId: '',
};

const EMPTY_SFTP_FORM: SftpFormState = {
  host: '',
  port: '22',
  username: '',
  password: '',
};

function grafxViewToForm(view: GraFxIntegrationSettingsView): GrafxFormState {
  return {
    authBaseUrl: view.authBaseUrl ?? '',
    grafxBaseUrl: view.grafxBaseUrl ?? '',
    clientId: view.clientId ?? '',
    clientSecret: '',
    environmentId: view.environmentId ?? '',
    dataConnectorId: view.dataConnectorId ?? '',
  };
}

function sftpViewToForm(view: SftpIntegrationSettingsView): SftpFormState {
  return {
    host: view.host ?? '',
    port: view.port != null ? String(view.port) : '22',
    username: view.username ?? '',
    password: '',
  };
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function secretHint(has: boolean | undefined, last4: string | null | undefined): string | undefined {
  if (!has) return undefined;
  return last4 ? `Currently set (ends in ${last4})` : 'Currently set';
}

export function SettingsPage() {
  const grafxQuery = useGrafxIntegrationQuery();
  const sftpQuery = useSftpIntegrationQuery();

  const loading = grafxQuery.isLoading || sftpQuery.isLoading;
  const error = grafxQuery.isError || sftpQuery.isError;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Per-tenant GraFx and SFTP integration credentials.
        </p>
      </div>

      {error ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-6 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            Failed to load integration settings.
          </CardContent>
        </Card>
      ) : null}

      {loading ? <Skeleton className="h-96 w-full" /> : null}

      {!loading && !error ? (
        <div className="space-y-6">
          <GrafxSettingsCard />
          <SftpSettingsCard />
        </div>
      ) : null}
    </div>
  );
}

function GrafxSettingsCard() {
  const query = useGrafxIntegrationQuery();
  const mutation = useUpdateGrafxIntegrationMutation();
  const [form, setForm] = useState<GrafxFormState>(EMPTY_GRAFX_FORM);
  const view = query.data?.settings ?? null;
  const configured = query.data?.configured ?? false;

  useEffect(() => {
    setForm(view ? grafxViewToForm(view) : EMPTY_GRAFX_FORM);
  }, [view]);

  const update = <K extends keyof GrafxFormState>(key: K, value: GrafxFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const body: GraFxIntegrationSettingsInput = {
      authBaseUrl: nullable(form.authBaseUrl),
      grafxBaseUrl: nullable(form.grafxBaseUrl),
      clientId: nullable(form.clientId),
      environmentId: nullable(form.environmentId),
      dataConnectorId: nullable(form.dataConnectorId),
    };
    if (form.clientSecret.length > 0) body.clientSecret = form.clientSecret;

    try {
      await mutation.mutateAsync(body);
      toast.success('GraFx settings saved');
    } catch (e) {
      toast.error('Save failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>GraFx integration</CardTitle>
          <CardDescription>
            {configured
              ? 'Credentials the workflow runner uses to reach GraFx Studio.'
              : 'Not configured yet. Fill in the credentials below to enable GraFx output.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Auth base URL" htmlFor="authBaseUrl">
              <Input
                id="authBaseUrl"
                type="url"
                value={form.authBaseUrl}
                onChange={(e) => update('authBaseUrl', e.target.value)}
              />
            </Field>
            <Field label="GraFx base URL" htmlFor="grafxBaseUrl">
              <Input
                id="grafxBaseUrl"
                type="url"
                value={form.grafxBaseUrl}
                onChange={(e) => update('grafxBaseUrl', e.target.value)}
              />
            </Field>
            <Field label="Environment ID" htmlFor="environmentId">
              <Input
                id="environmentId"
                value={form.environmentId}
                onChange={(e) => update('environmentId', e.target.value)}
              />
            </Field>
            <Field label="Data Connector ID" htmlFor="dataConnectorId">
              <Input
                id="dataConnectorId"
                value={form.dataConnectorId}
                onChange={(e) => update('dataConnectorId', e.target.value)}
              />
            </Field>
            <Field label="Client ID" htmlFor="clientId">
              <Input
                id="clientId"
                value={form.clientId}
                onChange={(e) => update('clientId', e.target.value)}
              />
            </Field>
            <Field
              label="Client secret"
              htmlFor="clientSecret"
              hint={secretHint(view?.hasClientSecret, view?.clientSecretLast4)}
            >
              <Input
                id="clientSecret"
                type="password"
                autoComplete="new-password"
                value={form.clientSecret}
                onChange={(e) => update('clientSecret', e.target.value)}
                placeholder={
                  view?.hasClientSecret ? 'Leave blank to keep current secret' : 'Enter client secret'
                }
              />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <KeyRound className="mr-1 h-3.5 w-3.5" />
              )}
              Save GraFx settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}

function SftpSettingsCard() {
  const query = useSftpIntegrationQuery();
  const mutation = useUpdateSftpIntegrationMutation();
  const [form, setForm] = useState<SftpFormState>(EMPTY_SFTP_FORM);
  const view = query.data?.settings ?? null;
  const configured = query.data?.configured ?? false;

  useEffect(() => {
    setForm(view ? sftpViewToForm(view) : EMPTY_SFTP_FORM);
  }, [view]);

  const update = <K extends keyof SftpFormState>(key: K, value: SftpFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const port = Number(form.port);
    const body: SftpIntegrationSettingsInput = {
      host: nullable(form.host),
      port: Number.isInteger(port) && port > 0 ? port : null,
      username: nullable(form.username),
      credentialId: view?.credentialId ?? 'default',
    };
    if (form.password.length > 0) body.password = form.password;

    try {
      await mutation.mutateAsync(body);
      toast.success('SFTP settings saved');
    } catch (e) {
      toast.error('Save failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>SFTP connection</CardTitle>
          <CardDescription>
            {configured
              ? 'Shared SFTP host and credentials for this tenant. Pickup and delivery folders are configured per flow.'
              : 'Not configured yet. Set the connection details below; folder paths are edited on each flow.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Host" htmlFor="sftpHost" required>
              <Input
                id="sftpHost"
                value={form.host}
                onChange={(e) => update('host', e.target.value)}
                placeholder="sftp.example.com"
              />
            </Field>
            <Field label="Port" htmlFor="sftpPort">
              <Input
                id="sftpPort"
                type="number"
                min={1}
                max={65535}
                value={form.port}
                onChange={(e) => update('port', e.target.value)}
              />
            </Field>
            <Field label="Username" htmlFor="sftpUsername" required>
              <Input
                id="sftpUsername"
                value={form.username}
                onChange={(e) => update('username', e.target.value)}
              />
            </Field>
            <Field
              label="Password"
              htmlFor="sftpPassword"
              hint={secretHint(view?.hasPassword, view?.passwordLast4)}
            >
              <Input
                id="sftpPassword"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                placeholder={
                  view?.hasPassword ? 'Leave blank to keep current password' : 'Enter password'
                }
              />
            </Field>
          </div>
          {view?.hasPrivateKey ? (
            <p className="text-xs text-muted-foreground">
              A private key is configured for this credential (key-based auth is supported).
            </p>
          ) : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Server className="mr-1 h-3.5 w-3.5" />
              )}
              Save SFTP settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  required,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
